import {i18n} from "./i18n";
import * as mdc from "material-components-web";
import UrlHelpers from "./url";

export class TenantMove {
    private readonly _organizationListId = 'tenantMoveOrganizationList';
    private readonly _i18n: i18n;
    private readonly _dapi: Dapi;

    private _mdcProgressBars: { [id: string]: mdc.linearProgress.MDCLinearProgress } = {};
    private _mdcTextFields: { [id: string]: mdc.textField.MDCTextField } = {};
    private _mdcLists: { [id: string]: mdc.list.MDCList } = {};
    private _mdcDialogs: { [id: string]: mdc.dialog.MDCDialog } = {};

    private _delayTimer: number;

    constructor() {
        this._dapi = (<any>window).dapi;
        this._i18n = new i18n();

        this.loadMaterial();
        this.setup();

        this.loadOrganizationList(
            this.getOrganizationListUrl(),
            this.mergeData,
            e => {
                this.openErrorDialogWithMessage(this._i18n.getTranslation('tenantMove_error_alert_message_misc'));
                console.debug('got error:', e);
            }
        )
        this.clearSearchInputHelperText();
        window.addEventListener('load', () => {
            this.showPageContent();
        })
    }

    private showPageContent = () => {
        const content = <HTMLElement>document.getElementById('tenantMove_content');
        if (!content) {
            console.log('cannot find element \'content\'');
            return;
        }
        content.style.display = '';
    };

    private loadMaterial = (): void => {
        const textfields = document.querySelectorAll('.mdc-text-field');
        Array.apply(null, textfields).forEach((textfield) => {
            const mdcTextfield = mdc.textField.MDCTextField.attachTo(textfield);
            //take input or textarea id if wrapping div has no id itself
            if (!textfield.id) {
                const inputs = textfield.getElementsByTagName('input');
                if (inputs.length > 0 && inputs[0].id) {
                    this._mdcTextFields[inputs[0].id] = mdcTextfield;
                    return
                }
                const areas = textfield.getElementsByTagName('textarea');
                if (areas.length > 0 && areas[0].id) {
                    this._mdcTextFields[areas[0].id] = mdcTextfield;
                    return
                }
                return
            }
            this._mdcTextFields[textfield.id] = mdcTextfield;
        });

        const uLists = document.querySelectorAll('.mdc-list');
        Array.apply(null, uLists).forEach((uList) => {
            const mdcList = mdc.list.MDCList.attachTo(uList);
            if (uList.id) {
                this._mdcLists[uList.id] = mdcList;
            }
        });

        const progressBars = document.querySelectorAll('.mdc-linear-progress');
        Array.apply(null, progressBars).forEach((pBar: HTMLElement) => {
            const mdcBar = mdc.linearProgress.MDCLinearProgress.attachTo(pBar);
            if (pBar.id) {
                this._mdcProgressBars[pBar.id] = mdcBar;
            }
        });

        const dialogs = document.querySelectorAll('.mdc-dialog');
        Array.apply(null, dialogs).forEach((d: HTMLElement) => {
            const mdcDialog = mdc.dialog.MDCDialog.attachTo(d);
            if (d.id) {
                this._mdcDialogs[d.id] = mdcDialog;
            }
        });
    };

    private getOrganizationListUrl = (search?: string): string => {
        const linkRelHref = UrlHelpers.getLinkRelHref('organizations');

        if (!search) {
            return linkRelHref;
        }

        let uri = new URL(linkRelHref);
        uri.searchParams.set('search', search);
        return uri.toString();
    };

    private mergeData = (data: organizationListDto): void => {
        if (!data) {
            console.log("mergeData: data is empty");
            return;
        }

        const current = this.readOrganizationList();
        current._links = data._links;
        if (!data.organizations) {
            console.log("mergeData: organizations is empty");
            this.writeOrganizationList(current);
            return
        }
        data.organizations.forEach((orga: organizationItemDto) => {
            const i = current.organizations.findIndex(o => o.id == orga.id);
            if (i == -1) {
                current.organizations.push(orga);
                return;
            }
        });
        this.writeOrganizationList(current);
        this.renderHelperText();
    };

    private setup = (): void => {
        const list = document.getElementById(this._organizationListId);
        if (!list) {
            console.error('organization list missing');
            return;
        }

        const dataAttributeName = 'data-data';
        const selectedOrganizationAttribute = 'data-selectedorganization';
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type == 'attributes' && mutation.attributeName == dataAttributeName) {
                    this.handleAttributeMutation();
                }
                if (mutation.type == 'attributes' && mutation.attributeName == selectedOrganizationAttribute) {
                    this.renderMoveButton();
                }
            });
        });
        observer.observe(list, {
            attributeFilter: [dataAttributeName, selectedOrganizationAttribute]
        });

        this.setupMoveButton();
        this.setupMoreButton();
        this.setupTextFieldSearch();
        this.setupConfirmationDialog();
        this.closeProgressBar();
    };

    private setupMoveButton = (): void => {
        const acceptBtn = <HTMLButtonElement>document.getElementById('tenantMove_accept');
        if (!acceptBtn) {
            console.error('element with id "tenantMove_accept" not found');
            return;
        }
        acceptBtn.addEventListener('click', () => {
            this.openConfirmationDialog();
        });
        acceptBtn.disabled = true;
    };

    private setupMoreButton = (): void => {
        const id = 'tenantMove_btnMore';
        const btn = <HTMLButtonElement>document.getElementById(id);
        if (!btn) {
            console.error(`setupMoreButton: element with id '${id}' not found`);
            return;
        }
        btn.addEventListener('click', () => {
            const data = this.readOrganizationList();
            if (!data || !data._links || !data._links.hasOwnProperty('more') || !data._links['more'].href) {
                console.error(`setupMoreButton: 'more' link not found`);
                return;
            }
            this.loadOrganizationList(
                data._links['more'].href,
                this.mergeData,
                e => {
                    this.openErrorDialogWithMessage(this._i18n.getTranslation('tenantMove_error_alert_message_misc'));
                    console.debug('more button event: got error:', e);
                }
            )
        });
    };

    private setupTextFieldSearch = (): void => {
        const searchInputMdc = this._mdcTextFields['tenantMove_searchOrga'];
        if (!searchInputMdc) {
            console.error('search input material missing');
            return;
        }
        const searchInput = searchInputMdc.root || searchInputMdc._root;
        if (!searchInput) {
            console.error('search input missing');
            return;
        }
        searchInput.addEventListener('input', ev => {
            clearTimeout(this._delayTimer);
            this._delayTimer = setTimeout(() => {
                this.filterList();
            }, 250);
        });
    };

    private setupConfirmationDialog = (): void => {
        const id = 'tenantMove_confirmationDialog';
        const dialog = this._mdcDialogs[id];
        if (!dialog) {
            console.error(`setupConfirmationDialog: dialog '${id}' missing`);
            return;
        }
        dialog.listen('MDCDialog:closed', (e: mdc.dialog.MDCDialogCloseEventDetail) => {
            if (e.detail.action == 'confirm') {
                this.moveTenant(() => {
                        this._dapi.closeResourceIfMainResource();
                    }
                );
            }
        });
    };

    private handleAttributeMutation = (): void => {
        this.renderList();
        this.renderMoreButton();
    };

    private filterList = (): void => {
        const input = this._mdcTextFields['tenantMove_searchOrga'];
        if (!input) {
            console.error('search input missing');
            return;
        }
        this.clearData();
        this.loadOrganizationList(
            this.getOrganizationListUrl(input.value),
            this.mergeData,
            e => {
                this.openErrorDialogWithMessage(this._i18n.getTranslation('tenantMove_error_alert_message_misc'));
                console.debug('got error:', e);
            }
        )
    };

    private clearData = (): void => {
        const ul = document.getElementById(this._organizationListId);
        if (!ul) {
            console.error(`cannot find element with id ${this._organizationListId}`);
            return
        }
        ul.dataset.data = '';
        ul.dataset.selectedorganization = '';
    }

    private openProgressBar = () => {
        const pbar = this._mdcProgressBars['tenantMove_loading_progressbar'];
        pbar.open();
    };

    private closeProgressBar = () => {
        const pbar = this._mdcProgressBars['tenantMove_loading_progressbar'];
        pbar.close();
    };

    private loadOrganizationList = (url: string, cbSuccess: (data: organizationListDto) => void, cbError: (e) => void): void => {
        this.openProgressBar();
        const req = new Request(url, {
            method: 'GET',
            headers: {
                'accept': 'application/hal+json'
            },
            credentials: 'same-origin'
        });

        fetch(req)
            .then((response => {
                this.closeProgressBar();
                if (response.ok) {
                    response.json()
                        .then((data: organizationListDto) => {
                            cbSuccess(data);
                        })
                        .catch(reason => {
                            console.error(`load organization list: cannot get json from response: ${reason}`);
                            cbError(reason);
                        });
                } else {
                    response.text().then(value => {
                        console.error(`load organization list failed with status ${response.status} - ${response.statusText}: ${value}`);
                        cbError(`load organization list failed`);
                    }).catch(reason => {
                        console.error(`load organization list failed with status ${response.status} - ${response.statusText}: ${reason}`);
                        cbError(`load organization list failed`);
                    })
                }
            }))
            .catch(reason => {
                this.closeProgressBar();
                cbError(reason);
            });
    };

    private writeOrganizationList = (data: organizationListDto): void => {
        const ul = <HTMLUListElement>document.getElementById(this._organizationListId);
        if (!ul) {
            console.error(`cannot find element with id ${this._organizationListId}`);
            return
        }
        ul.dataset.data = JSON.stringify(data);
    };

    private readOrganizationList = (): organizationListDto => {
        const ul = <HTMLUListElement>document.getElementById(this._organizationListId);
        if (!ul) {
            console.error(`cannot find element with id ${this._organizationListId}`);
            return null;
        }
        const data = ul.dataset.data;
        if (!data) {
            return {
                organizations: []
            } as organizationListDto
        }
        return JSON.parse(data);
    };

    private renderMoveButton = (): void => {
        const button = <HTMLButtonElement>document.getElementById('tenantMove_accept');
        if (!button) {
            console.error('element with id "tenantMove_accept" not found');
            return;
        }
        const ul = document.getElementById(this._organizationListId);
        if (!ul) {
            console.debug(`renderMoveButton: cannot find ul element with id ${this._organizationListId}`);
            return;
        }
        const orgaSelected = !!ul.dataset.selectedorganization
        button.disabled = !orgaSelected;
    };

    private renderList = (): void => {
        const data = this.readOrganizationList();
        if (!data) {
            console.debug("render: data missing");
            return;
        }

        const ul = document.getElementById(this._organizationListId);
        if (!ul) {
            console.error(`render: cannot find ul element with id ${this._organizationListId}`);
            return;
        }

        const collectionUl = document.createElement('ul');
        data.organizations.forEach((orga, index) => {
            const li = this.getListItem(orga)
            const selectedOrgaId = ul.dataset.selectedorganization;
            if (selectedOrgaId && selectedOrgaId == li.dataset.value) {
                li.classList.add('mdc-list-item--selected');
                li.setAttribute('aria-selected', 'true');
            }

            collectionUl.appendChild(li);
        });

        ul.innerHTML = collectionUl.innerHTML;
        this.resetList();
    };

    private renderMoreButton = (): void => {
        const wrapperId = 'tenantMove_btnMore_wrapper';
        const wrapper = <HTMLElement>document.getElementById(wrapperId);
        if (!wrapper) {
            console.error(`renderMoreButton: element with id '${wrapperId}' not found`);
            return;
        }
        const orgaData = this.readOrganizationList();
        const showButton = orgaData && orgaData._links && orgaData._links.hasOwnProperty('more');
        wrapper.style.display = showButton ? '' : 'none';
    };

    private renderHelperText = (): void => {
        const data = this.readOrganizationList();
        if (!data) {
            console.debug("renderHelperText: data missing");
            return;
        }
        data.organizations.length == 0 ? this.setSearchInputHelperText(this._i18n.getTranslation('tenantMove_error_no_organization_found')) : this.clearSearchInputHelperText();
    };

    private getListItem = (organization: organizationItemDto): HTMLElement => {
        const li = document.createElement('li');
        li.classList.add('mdc-list-item');
        li.dataset.value = organization.id;
        li.dataset.organizationname = organization.name;
        li.setAttribute('role', 'menuitem');
        li.innerHTML = `<span class="mdc-list-item__ripple"></span>
						<span class="mdc-list-item__text">
							<span class="mdc-list-item__primary-text">${organization.name}</span>
							<span class="mdc-list-item__secondary-text">ID: ${organization.id}</span>
						</span>`
        return li;
    };

    private resetList = (): void => {
        const mdcList: mdc.list.MDCList = this._mdcLists[this._organizationListId];
        if (!mdcList) {
            console.log(`resetList: cannot find mdcList with id ${this._organizationListId}`);
            return
        }

        mdcList.destroy();

        const newMdcList: mdc.list.MDCList = mdc.list.MDCList.attachTo(document.getElementById(this._organizationListId));
        newMdcList.listen('MDCList:action', this.handleListItemClick);

        this._mdcLists[this._organizationListId] = newMdcList;
    };

    private handleListItemClick = (ev): void => {
        const index = ev.detail.index;
        const mdcList: mdc.list.MDCList = this._mdcLists[this._organizationListId];
        if (!mdcList) {
            console.error('handleListItemClick: mdc list not found');
            return;
        }
        const listItem = mdcList.listElements[index];
        if (!listItem) {
            console.error('handleListItemClick: list item not found');
            return;
        }
        const ul = mdcList.root || mdcList._root;
        if (!ul) {
            console.error('handleListItemClick: ul not found');
            return;
        }
        ul.dataset.selectedorganization = listItem.dataset.value;
    };

    private openErrorDialogWithMessage = (message: string): void => {
        const dialog = this._mdcDialogs['tenantMove_errorDialog'];
        if (!dialog) {
            console.error('error dialog not found');
            return;
        }
        const dialogContent = <HTMLElement>document.getElementById('tenantMove_errorDialog_content');
        if (!dialogContent) {
            console.error('dialog content element not found');
            return;
        }
        dialogContent.innerText = message;
        dialog.open();
    };

    private openConfirmationDialog = (): void => {
        const dialog = this._mdcDialogs['tenantMove_confirmationDialog'];
        if (!dialog) {
            console.error('openConfirmationDialog: confirmation dialog not found');
            return;
        }
        const dialogContent = <HTMLElement>document.getElementById('tenantMove_confirmationDialog_content');
        if (!dialogContent) {
            console.error('openConfirmationDialog: dialog content element not found');
            return;
        }
        const selectedOrgaId = this.readSelectedOrganization();
        const o = this.getDataForOrganizationId(selectedOrgaId);
        if (!o) {
            console.error(`openConfirmationDialog: missing data for organization id '${selectedOrgaId}'`);
            return;
        }
        const anchor = <HTMLAnchorElement>document.getElementById('tenantMove_confirmationDialog_anchor');
        if (!anchor) {
            console.error('openConfirmationDialog: anchor missing');
            return;
        }
        anchor.href = `${UrlHelpers.getLinkRelHref("organization")}/${o.id}`;
        anchor.innerText = o.name;
        dialog.open();
    };

    private setSearchInputHelperText = (content: string): void => {
        const textField = this._mdcTextFields['tenantMove_searchOrga'];
        if (!textField) {
            console.error('setSearchInputHelperText: mdc text field not found');
            return;
        }
        textField.helperTextContent = content;
    };

    private clearSearchInputHelperText = (): void => {
        this.setSearchInputHelperText('');
    };

    private getTargetOrganizationId = (): string => {
        const list = document.getElementById(this._organizationListId);
        if (!list) {
            console.error('organization list missing');
            return;
        }
        return list.dataset.selectedorganization;
    };

    private moveTenant = (cbSuccess: () => void): void => {
        const url = UrlHelpers.getLinkRelHref('tenant');
        const targetOrgaId = this.getTargetOrganizationId();
        if (!targetOrgaId) {
            console.error('target organization id not found');
            return;
        }
        const req = new Request(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/merge-patch+json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                tenant: {
                    organizationId: targetOrgaId
                }
            })
        });

        fetch(req)
            .then((response => {
                console.debug('response:', response);
                if (!response.ok) {
                    this.openErrorDialogWithMessage(this.getMoveTenantErrorMessageForStatus(response.status));
                    return;
                }
                cbSuccess();
            }))
            .catch(reason => {
                console.debug('move tenant failed with error:',reason)
                this.openErrorDialogWithMessage(this._i18n.getTranslation('tenantMove_error_alert_message_misc'));
            });
    };

    private readSelectedOrganization = (): string => {
        const mdcList: mdc.list.MDCList = this._mdcLists[this._organizationListId];
        if (!mdcList) {
            console.error('handleListItemClick: mdc list not found');
            return;
        }
        const ul = mdcList.root || mdcList._root;
        if (!ul) {
            console.error('handleListItemClick: ul not found');
            return;
        }
        return ul.dataset.selectedorganization ? ul.dataset.selectedorganization : '';
    };

    private getDataForOrganizationId = (id: string): organizationItemDto => {
        const data = this.readOrganizationList();
        return data.organizations.find(e => e.id == id);
    };

    private getMoveTenantErrorMessageForStatus = (status: number): string =>  {
        let key = 'tenantMove_error_alert_message_couldNotMove';
        const keyMap = {
            404: 'tenantMove_error_alert_message_notFound',
            403: 'tenantMove_error_alert_message_forbidden',
            409: 'tenantMove_error_alert_message_conflict'
        }
        if (keyMap.hasOwnProperty(status)) {
            key = keyMap[status];
        }
        return this._i18n.getTranslation(key);
    }
}

export interface organizationListDto {
    _links: { [key: string]: linkRel }
    organizations: organizationItemDto[]
}

export interface linkRel {
    href: string
}

export interface organizationItemDto {
    id: string
    name: string
    contact: string
    icon: string
    _links: { [key: string]: linkRel }
}
